import { useTranslation } from 'react-i18next'
import type { TimelineVisualAdjustmentsDict } from '../../../../generated/nodeDataTypes'
import type { TimelineClip } from './types'

type NumberSetting = Exclude<keyof TimelineVisualAdjustmentsDict, 'flip_horizontal' | 'flip_vertical'>
const settings: { key: NumberSetting; min: number; max: number; step: number; initial: number; group: 'color' | 'frame' }[] = [
  { key: 'brightness', min: -1, max: 1, step: 0.01, initial: 0, group: 'color' },
  { key: 'contrast', min: 0, max: 3, step: 0.01, initial: 1, group: 'color' },
  { key: 'saturation', min: 0, max: 3, step: 0.01, initial: 1, group: 'color' },
  { key: 'scale', min: 0.25, max: 3, step: 0.01, initial: 1, group: 'frame' },
  { key: 'position_x', min: -1, max: 1, step: 0.01, initial: 0, group: 'frame' },
  { key: 'position_y', min: -1, max: 1, step: 0.01, initial: 0, group: 'frame' },
  { key: 'rotation', min: -180, max: 180, step: 1, initial: 0, group: 'frame' },
]
const buttonClass = 'h-7 rounded-lg border border-input-border bg-surface-1 px-2 text-xs font-medium hover:bg-surface-3 hover:border-input-border-hover focus-visible:ring-2 focus-visible:ring-primary'

export function BasicClipControls({ clip, onChange, onTimeEdit, timeEditBusy, timeEditError, playhead = 0 }: {
  clip: TimelineClip
  onChange: (id: string, adjustments: TimelineVisualAdjustmentsDict, fadeColor?: 'black' | 'white') => void
  onTimeEdit?: (clip: TimelineClip, mode: 'reverse' | 'freeze') => void
  timeEditBusy?: boolean
  timeEditError?: string | null
  playhead?: number
}) {
  const { t } = useTranslation('canvas')
  const values = clip.adjustments ?? {}
  return <div className="space-y-2" data-testid="timeline-basic-controls">
    {onTimeEdit && <details open className="rounded-lg border border-border">
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium hover:bg-surface-2">{t('timelineOverhaul.time')}</summary>
      <div className="space-y-2 px-3 pb-3">
        <div className="flex gap-2">
          <button type="button" disabled={timeEditBusy} className={`${buttonClass} disabled:opacity-40`} onClick={() => onTimeEdit(clip, 'reverse')}>{t('timelineOverhaul.reverse')}</button>
          <button type="button" disabled={timeEditBusy || playhead < clip.startOffset || playhead >= clip.startOffset + clip.effectiveDuration}
            className={`${buttonClass} disabled:opacity-40`} onClick={() => onTimeEdit(clip, 'freeze')}>{t('timelineOverhaul.freeze')}</button>
        </div>
        <p className="text-xs font-medium text-muted-foreground">{t(timeEditBusy ? 'timelineOverhaul.processing' : 'timelineOverhaul.freezeHint')}</p>
        {timeEditError && <p role="alert" className="text-xs font-medium text-destructive">{timeEditError}</p>}
      </div>
    </details>}
    {(['frame', 'color'] satisfies ('frame' | 'color')[]).map((group) => <details key={group} className="rounded-lg border border-border" open={group === 'frame'}>
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium hover:bg-surface-2">{group === 'frame' ? t('timelineCurves.transform', 'Transform') : t('timelineOverhaul.color')}</summary>
      <div className="space-y-3 px-3 pb-3">
        {settings.filter((setting) => setting.group === group).map((setting) => <div key={setting.key} className="block text-xs font-medium text-muted-foreground">
          <div className="flex items-center justify-between gap-2"><span>{t(`timelineOverhaul.${setting.key}`)}</span>
            {group === 'frame' ? <div className="flex items-center gap-1">
              <input type="number" aria-label={`${t(`timelineOverhaul.${setting.key}`)} ${t('timelineCurves.value', 'value')}`}
                className="h-7 w-20 rounded-lg border border-input-border bg-input-bg px-2 text-foreground focus:ring-2 focus:ring-primary"
                min={setting.min * (setting.key === 'rotation' ? 1 : 100)} max={setting.max * (setting.key === 'rotation' ? 1 : 100)} step={1}
                value={Math.round((values[setting.key] ?? setting.initial) * (setting.key === 'rotation' ? 1 : 100))}
                onChange={(event) => {
                  const value = event.target.valueAsNumber / (setting.key === 'rotation' ? 1 : 100)
                  if (Number.isFinite(value) && value >= setting.min && value <= setting.max) onChange(clip.id, { ...values, [setting.key]: value })
                }} />{setting.key === 'rotation' ? '°' : '%'}
            </div> : <output className="tabular-nums">{(values[setting.key] ?? setting.initial).toFixed(2)}</output>}
          </div>
          <input type="range" className="mt-2 w-full accent-primary" min={setting.min} max={setting.max} step={setting.step}
            aria-label={t(`timelineOverhaul.${setting.key}`)} value={values[setting.key] ?? setting.initial}
            onChange={(event) => onChange(clip.id, { ...values, [setting.key]: Number(event.target.value) })} />
        </div>)}
        {group === 'frame' && <p className="text-xs font-medium text-text-muted">{t('timelineCurves.transformHint', 'Uniform scale. Position is relative to half the frame width or height.')}</p>}
        {group === 'frame' && <div className="flex gap-2">
          {(['flip_horizontal', 'flip_vertical'] satisfies ('flip_horizontal' | 'flip_vertical')[]).map((key) => <button key={key} type="button" className={buttonClass}
            aria-pressed={values[key] ?? false} onClick={() => onChange(clip.id, { ...values, [key]: !values[key] })}>{t(`timelineOverhaul.${key}`)}</button>)}
        </div>}
        {group === 'frame' && <button type="button" className={buttonClass} onClick={() => onChange(clip.id, {
          ...values, scale: 1, position_x: 0, position_y: 0, rotation: 0, flip_horizontal: false, flip_vertical: false,
        })}>{t('timelineCurves.resetTransform', 'Reset transform')}</button>}
      </div>
    </details>)}
    <label className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground">
      {t('timelineOverhaul.fadeColor')}
      <select className="h-7 rounded-lg border border-input-border bg-input-bg px-2 text-foreground focus:ring-2 focus:ring-primary"
        value={clip.fadeColor ?? 'black'} onChange={(event) => {
          const value = event.target.value
          if (value !== 'black' && value !== 'white') throw new Error('Invalid transition color')
          onChange(clip.id, values, value)
        }}>
        <option value="black">{t('timelineOverhaul.black')}</option>
        <option value="white">{t('timelineOverhaul.white')}</option>
      </select>
    </label>
    <button type="button" className={buttonClass} onClick={() => onChange(clip.id, {}, 'black')}>{t('timelineOverhaul.resetAppearance')}</button>
  </div>
}
